import { useEffect, useState } from "react";
import { Loader2, Users, LogIn, BookOpen, CheckCircle2, Sparkles, Activity } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { adminStats, adminActivity, adminPopularTerms } from "@/lib/inmindApi";
import CategoryBadge from "@/components/CategoryBadge";

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, p] = await Promise.all([adminStats(), adminActivity(), adminPopularTerms()]);
        setStats(s); setActivity(a); setPopular(p);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !stats) {
    return <div className="flex items-center justify-center py-24 text-[var(--inmind-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Tile icon={Users} label="Users" value={stats.total_users} testId="stat-total-users" />
        <Tile icon={Activity} label="Active 7d" value={stats.active_users_7d} testId="stat-active-7d" />
        <Tile icon={LogIn} label="Logins" value={stats.total_logins} testId="stat-total-logins" />
        <Tile icon={BookOpen} label="Terms" value={stats.total_terms} testId="stat-terms" />
        <Tile icon={CheckCircle2} label="Reviews" value={stats.total_reviews} testId="stat-reviews" />
        <Tile icon={Sparkles} label="Think today" value={stats.ai_messages_today} testId="stat-ai-today" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
          <CardContent className="p-5">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-3">
              Most-sent cards
            </div>
            {popular.length === 0 ? (
              <p className="text-sm text-[var(--inmind-text-secondary)]">No data yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="popular-terms">
                {popular.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 text-sm" data-testid={`popular-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--inmind-text)] truncate">{t.term}</div>
                      <CategoryBadge category={t.category} />
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-serif text-lg text-[var(--inmind-text)] tabular-nums">{t.sent_count}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--inmind-muted)]">sent</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
          <CardContent className="p-5">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-3">
              Recent logins
            </div>
            {activity.length === 0 ? (
              <p className="text-sm text-[var(--inmind-text-secondary)]">No activity yet.</p>
            ) : (
              <ul className="space-y-2.5" data-testid="recent-activity">
                {activity.slice(0, 15).map((a) => (
                  <li key={a.id} className="flex items-center gap-3 text-sm" data-testid={`activity-${a.id}`}>
                    <LogIn className="w-3.5 h-3.5 text-[var(--inmind-primary)]" strokeWidth={1.7} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--inmind-text)] truncate">{a.name || a.email}</div>
                      <div className="text-xs text-[var(--inmind-text-secondary)] truncate">
                        {a.email}
                        {a.provider && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-black/5 uppercase text-[9px] tracking-wider">{a.provider}</span>}
                      </div>
                    </div>
                    <span className="text-xs text-[var(--inmind-text-secondary)] tabular-nums">
                      {new Date(a.ts).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
      <CardContent className="p-4">
        <Icon className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.6} />
        <div className="font-serif text-3xl mt-2 text-[var(--inmind-text)] tabular-nums" data-testid={testId}>{value}</div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-muted)] mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
